import type { ComputedRef } from 'vue'
import type { AnalyticsTracker } from '@/tracking/analytics/analyticsTracker'
import type { AuditResult } from '@/content-review/api'
import { IMPACT_OF_DISABILITY_EVENT_NAMES } from '@/impact-of-disability/event_names'

export type CriteriaEventAttrs = {
  criteria_label: string
  is_custom_criteria_set: boolean
}

export function calculateNormalizedScores(results: { score: number; max_score: number; section: string }[]) {
  const totals: Record<string, { totalScore: number; count: number; averageScore?: number }> = {}
  for (const r of results) {
    const normalized = r.max_score > 0 ? (r.score / r.max_score) * 100 : 0
    if (!totals[r.section]) totals[r.section] = { totalScore: 0, count: 0 }
    totals[r.section].totalScore += normalized
    totals[r.section].count++
  }
  for (const key of Object.keys(totals)) {
    const s = totals[key]
    s.averageScore = Number((s.count > 0 ? s.totalScore / s.count : 0).toFixed(2))
  }
  return totals
}

export function useImpactStatementReviewAnalytics(
  analytics: AnalyticsTracker,
  criteriaEventAttrs: ComputedRef<CriteriaEventAttrs>,
  hasResults: ComputedRef<boolean>
) {
  function handleAutoReviewButtonClick(onOpen: () => void) {
    analytics.sendEvent(IMPACT_OF_DISABILITY_EVENT_NAMES.CLICKED_AUTO_REVIEW_BUTTON, {
      has_existing_results: hasResults.value,
      ...criteriaEventAttrs.value,
    })
    onOpen()
  }

  function handleModalReview(onReview: () => void) {
    analytics.sendEvent(IMPACT_OF_DISABILITY_EVENT_NAMES.CLICKED_AUTO_REVIEW_MODAL_REVIEW, criteriaEventAttrs.value)
    onReview()
  }

  function handleCloseReviewResults(onClose: () => void) {
    analytics.sendEvent(IMPACT_OF_DISABILITY_EVENT_NAMES.CLOSED_REVIEW_RESULTS, criteriaEventAttrs.value)
    onClose()
  }

  function handleReviewResultsToggle(result: { section: string; audit_title: string; score: number; max_score: number }, expanded: boolean) {
    if (expanded) {
      analytics.sendEvent(IMPACT_OF_DISABILITY_EVENT_NAMES.CLICKED_REVIEW_RESULTS_ITEM_TOGGLE, {
        section: result.section,
        criteria_title: result.audit_title,
        score: result.score,
        max_score: result.max_score,
        ...criteriaEventAttrs.value,
      })
    }
  }

  function handleReceivedReviewResults(results: AuditResult[]) {
    analytics.sendEvent(IMPACT_OF_DISABILITY_EVENT_NAMES.RECEIVED_REVIEW_RESULTS, {
      impact_audit_scores: results.map((r) => ({
        criteria_title: r.audit_title,
        section: r.section,
        score: r.score,
        max_score: r.max_score,
      })),
      average_normalized_scores: calculateNormalizedScores(results),
      ...criteriaEventAttrs.value,
    })
  }

  return {
    handleAutoReviewButtonClick,
    handleModalReview,
    handleCloseReviewResults,
    handleReviewResultsToggle,
    handleReceivedReviewResults,
  }
}
